import { useCallback, useEffect, useRef } from 'react';
import { getDashboard, getSummary, Dashboard, Summary } from '|/app/services';

// Custom event for requesting dashboard/summary refresh
const REFRESH_EVENT = 'purchase-ui:refresh-dashboard-summary';

/**
 * Dispatches a custom event to trigger dashboard and summary refresh
 */
export const triggerDashboardSummaryRefresh = () => {
  const event = new CustomEvent(REFRESH_EVENT);
  window.dispatchEvent(event);
};

/**
 * Hook that listens for dashboard/summary refresh events and executes the refresh
 * @param onDashboardUpdate - Callback when dashboard is updated
 * @param onSummaryUpdate - Callback when summary is updated
 */
export const useDashboardSummaryRefresh = (
  onDashboardUpdate?: (dashboard: Dashboard) => void,
  onSummaryUpdate?: (summary: Summary) => void
) => {
  // Use refs to store the callbacks to avoid recreating handleRefresh
  const onDashboardUpdateRef = useRef(onDashboardUpdate);
  const onSummaryUpdateRef = useRef(onSummaryUpdate);

  // Update refs when callbacks change
  useEffect(() => {
    onDashboardUpdateRef.current = onDashboardUpdate;
    onSummaryUpdateRef.current = onSummaryUpdate;
  }, [onDashboardUpdate, onSummaryUpdate]);

  const handleRefresh = useCallback(async () => {
    try {
      const [dashboard, summary] = await Promise.all([
        getDashboard().catch(() => null),
        getSummary().catch(() => null)
      ]);

      // Batch the state updates to reduce re-renders
      if (
        (dashboard !== null && onDashboardUpdateRef.current) ||
        (summary !== null && onSummaryUpdateRef.current)
      ) {
        // Use requestAnimationFrame to batch updates in the next frame
        requestAnimationFrame(() => {
          if (dashboard !== null && onDashboardUpdateRef.current) {
            // Create a new object with updated steps array to ensure React detects the change
            const updatedDashboard = {
              ...dashboard,
              steps: dashboard.steps ? [...dashboard.steps] : []
            };
            onDashboardUpdateRef.current(updatedDashboard);
          }
          if (summary !== null && onSummaryUpdateRef.current) {
            // Force a new object reference to ensure React detects the change
            onSummaryUpdateRef.current({ ...summary });
          }
        });
      }
    } catch (error) {
      // Silently handle errors
    }
  }, []); // No dependencies since we use refs

  useEffect(() => {
    const listener = () => {
      void handleRefresh();
    };
    window.addEventListener(REFRESH_EVENT, listener);
    return () => {
      window.removeEventListener(REFRESH_EVENT, listener);
    };
  }, [handleRefresh]);
};

/**
 * Hook that provides a function to trigger dashboard/summary refresh
 */
export const useRefreshDashboardSummary = () => {
  return useCallback(() => {
    triggerDashboardSummaryRefresh();
  }, []);
};
